// Help Center content: Quick-Start guide + FAQ shown in-app via <HelpCenter />.
// This is plain data, no logic. Edit the copy here; the component just renders it.
// Voice: warm, plain-English, written for brand-new / prospective coaches.

export interface QuickStartStep {
  title: string;
  body: string;
}

export interface FaqItem {
  q: string;
  a: string;
}

export interface FaqCategory {
  category: string;
  items: FaqItem[];
}

export const QUICK_START: QuickStartStep[] = [
  {
    title: 'Sign in',
    body: "Use Google or an email and password, whichever you like. If the Google button won't open, you're probably tapping a link inside a social app like Instagram or Facebook. Their built-in browser blocks Google sign-in. Tap the ⋯ menu and choose “Open in Safari” (or Chrome), or just sign in with email instead. That works everywhere.",
  },
  {
    title: 'Create your team (or join one)',
    body: "New head coach? Tap “Create a New Team” and pick three things: your Style of Play (T-Ball/Coach Pitch, Machine Pitch, or Players Pitch), your Age Group, and your League/Org. These shape which tools you see. Helping out another coach? Tap “Join a Team with Code” and enter the code they share with you.",
  },
  {
    title: 'Add your roster',
    body: 'Type your players in by hand, or, if you already use GameChanger, import a stats export and your roster fills in automatically. You can always add, edit, or remove players later from the Roster tab.',
  },
  {
    title: 'Scout your roster (optional)',
    body: "On Machine Pitch and Players Pitch teams, you can rate each player on a few simple attributes. That gives every player an OVR (your read on them, 0–99) and, once real games are in, powers xOVR. It's optional. Skip it and the rest of the app still works.",
  },
  {
    title: 'Sync your schedule',
    body: 'Paste your GameChanger calendar link and Dugout pulls in your whole season. No typing in game dates one at a time. You can also just add games manually if you prefer.',
  },
  {
    title: 'Plan and run your first game',
    body: "Open a game, set your starting pitcher, drag your batting order into place, and assign positions. When you're ready, tap “Play Ball!” to run it live and track at-bats, substitutions, and (for Players Pitch) pitch counts as the game happens.",
  },
];

export const FAQ: FaqCategory[] = [
  {
    category: 'Getting started',
    items: [
      {
        q: "The Google sign-in button won't work. What's wrong?",
        a: "You're almost certainly inside a social app's built-in browser (Instagram, Facebook, TikTok, etc.), and those block Google sign-in. Tap the ⋯ menu in the corner and choose “Open in Safari” or “Open in Chrome,” then sign in there. Or skip the hassle and sign in with your email and password. That always works.",
      },
      {
        q: 'Can I try Dugout before creating an account?',
        a: "Yes. Tap “Try a Live Demo” on the sign-in screen, no account needed. It's a full sample team you can poke around in. The demo resets itself automatically about every 2 hours, so explore freely.",
      },
      {
        q: "What's the difference between creating a team and joining one?",
        a: 'Create a team if you’re the head coach setting up your own roster. You’ll pick your style of play, age group, and league. Join a team if another coach has already set one up and wants your help; they’ll give you a join code to enter.',
      },
      {
        q: 'What is a join code?',
        a: "It's a short code the head coach shares so assistant coaches can hop onto the same team. The head coach finds it under Settings (or the “Add Assistant Coach” option in the account menu) and sends it over. The assistant enters it on the “Join a Team” screen and they're connected.",
      },
      {
        q: 'I forgot my password.',
        a: "On the email sign-in screen, tap “Forgot password?” and we'll email you a reset link. If you originally signed up with Google, there's no password to reset; just use the Google button.",
      },
    ],
  },
  {
    category: 'Coaches & roles',
    items: [
      {
        q: "What's the difference between a head coach and an assistant coach?",
        a: 'The head coach is whoever created the team. They can do everything: edit the roster, set team rules, scout players, and manage the subscription. Assistant coaches join with a code and can run live games and view all stats, but they can’t change team settings or edit player ratings.',
      },
      {
        q: 'Why is my Settings screen locked?',
        a: "If you joined as an assistant coach, Settings is read-only by design. Team setup stays in the head coach's hands so rules don't change out from under everyone. You can still run games and see all the stats.",
      },
      {
        q: 'Can I run multiple teams from one account?',
        a: "Absolutely. Perfect for an organization with several age groups (say 8U coach pitch, 10U kid pitch, and 6U tee-ball). Set up your first team, then open the account menu in the top-right corner and tap “Add Team” to run the same quick setup again. You can switch between teams seamlessly from that same menu. Tip: add a logo or photo to each team so you know which one you're in at a glance. There's no cap on how many teams you can add, as long as you're a coach on each one.",
      },
    ],
  },
  {
    category: 'Player ratings (OVR & xOVR)',
    items: [
      {
        q: 'What is OVR?',
        a: 'OVR is your coaching eye, turned into a single number from 0 to 99. You rate each player on a handful of simple attributes (hitting, fielding, and so on), and Dugout rolls those up into one overall rating you can compare across your roster.',
      },
      {
        q: 'What is xOVR, and how is it different from OVR?',
        a: "xOVR takes your OVR and checks it against what actually happens in real games. If a player is outperforming your rating, you'll see a 🔥 (they're hotter than you scouted); if they're underperforming, a ❄️. It only carries weight once there's enough game data to mean something; early on it stays modest on purpose. Think of it as the stats either backing up your eye or nudging it.",
      },
      {
        q: "What's the big idea behind xOVR, and why is it special?",
        a: "Borrowing from the magic of MLB The Show, Dugout gives every player an OVR (Overall Rating) built from your Coach Scouting Report: your honest, gut-level read of each player on a subjective 1–5 scale. Then the season starts. As real game stats roll in, Dugout blends what you see on the field with what the numbers say to produce xOVR. The “x” is for expected, the same idea behind baseball's sharpest stats, now working for your team. No clipboard can do this, and no other youth baseball app brings your eyes and the numbers together like Dugout. xOVR is your unbiased second opinion on every kid in the dugout, quietly flagging when you're under-rating the quiet kid who's secretly raking, or over-rating the preseason star whose bat hasn't caught up to the hype. And because Dugout tracks every player's xOVR game to game in the Roster panel, you don't just get a snapshot; you watch each player's growth and development unfold across the whole season.",
      },
      {
        q: 'Why don’t I see OVR or xOVR on my team?',
        a: "Player ratings turn on for Machine Pitch and Players Pitch teams. T-Ball and Coach Pitch skip them; at that level it's about fun and fair playing time, not scouting reports, so we keep those teams focused on lineups and fairness instead.",
      },
    ],
  },
  {
    category: 'Lineups & fair playing time',
    items: [
      {
        q: 'How does Dugout help keep playing time fair?',
        a: "The Field Time Equalizer tracks how many innings each player has spent on the bench and at each position across the whole season, not just one game. It helps you spot who's owed more time so nobody quietly gets shortchanged. It's free and works for every level, T-Ball through travel ball.",
      },
      {
        q: 'Does it build the lineup for me?',
        a: "It helps you build it fast. You set a strategy and Dugout suggests a batting order and positions; you drag anything you want to change. The app remembers position history between games so the rotation stays fair on its own.",
      },
      {
        q: 'How should I rate my players’ positions, and what does a 0 mean?',
        a: "Two different things get rated in Dugout. Your skill attributes (hitting, fielding, and so on) are your 1–5 read of each player; if you’ve just picked up players and don’t know them yet, it’s perfectly fine to set everyone to a middle 3 and adjust as you watch them play. Position ratings are different: they tell the app where each kid can play, and a 0 means “don’t put them here.” So only rate a player at the spots they’d actually play. If a kid never plays shortstop, give them a 0 at SS and the lineup builder won’t send them there. The position that matters most is catcher: because the fewest kids can catch, Dugout fills it first, but only from the players you’ve rated as catchers. So give a catcher rating only to the kids who actually catch, and set everyone else to 0 there. That keeps your real catchers in the rotation and stops a non-catcher from ending up behind the plate (and don’t worry, Dugout also protects your best catcher from being benched by the fairness rules).",
      },
      {
        q: 'Where do I turn fairness rules on or off?',
        a: 'In Settings (head coach only). You can require minimum fielding innings per game, make sure everyone sits before anyone sits twice, prevent repeat positions, and more. Turn on as many or as few as fit your team.',
      },
    ],
  },
  {
    category: 'Pitch counts & arm care',
    items: [
      {
        q: 'Does Dugout track pitch counts?',
        a: 'Yes, for Players Pitch teams. It counts pitches live during the game and follows the limits and rest-day rules for your league and age group (Little League, USSSA, Perfect Game, NFHS, or your own custom rules).',
      },
      {
        q: "What happens when a pitcher hits their limit or needs rest?",
        a: "Dugout warns you. For example, if a player threw enough to need a rest day, it'll flag that they're not available to pitch until a certain date. You're always in control: the warnings are there to protect arms, but you can override them if you need to.",
      },
      {
        q: 'Do tournaments use different pitch rules?',
        a: 'They can. Tournaments often run on their own pitch limits, separate from your regular-season rest rules. You can set tournament-specific rules so the counts stay accurate for that event.',
      },
    ],
  },
  {
    category: 'Stats & GameChanger',
    items: [
      {
        q: 'How do my stats get into Dugout?',
        a: "Two ways. Everyone can sync their GameChanger calendar to pull in the schedule. On Elite, you can also bring each finished game in from GameChanger: the scorebook PDF, the game stats PDF and the stats CSV, in any order. Dugout tells you as each one lands, checks they're all the same game, and fills in the box score, pitch counts, field time and final score.",
      },
      {
        q: 'How do I send a game from GameChanger?',
        a: "On iPhone, open each export in GameChanger, tap Share, then Import to Dugout. You'll see a checklist of what's arrived; tap Open Dugout when you're ready, or go back for the next file. On the web, open a game in Dugout and tap Import Data (or Import from GameChanger on the Games tab), then add the files or paste the stats. Nothing is saved until you tap Save to Dugout, and files you've sent wait for you until then.",
      },
      {
        q: 'Will it match the stats to the right players?',
        a: 'Yes. Dugout matches players by name automatically when you import. If a name is spelled differently or it isn’t sure, it’ll ask you to confirm before anything is saved.',
      },
      {
        q: "What's the difference between “Current Season” and “Prior Baseline” when I import?",
        a: 'Choose Current Season if these stats are for the season you’re coaching now; they become your live, tracked totals. Choose Prior Baseline if they’re from a past season you just want as background context; those stay separate and won’t mix with your new game stats.',
      },
    ],
  },
  {
    category: 'Pricing & Elite',
    items: [
      {
        q: "What's free, and what's Elite?",
        a: "Free covers the essentials: your roster, the lineup builder, fair-playing-time tracking, live game management with pitch counts, and GameChanger calendar sync. Elite adds the analytics: xOVR, hot/cold streaks, deeper stat insights, AI coaching suggestions, tournament mode, assistant-coach scouting, and per-game GameChanger stat imports.",
      },
      {
        q: 'How much does Elite cost?',
        a: "$9.99/month, or $4.99/month if you pay yearly (billed $59.99/year). Every Elite plan starts with a 14-day free trial: no charge today, and you can cancel anytime from Settings.",
      },
      {
        q: 'How do I cancel?',
        a: "Open the account menu and choose “Manage Subscription” to handle billing or cancel. If you cancel during the 14-day trial, you won't be charged.",
      },
      {
        q: 'What is a Founding Coach? What’s the waitlist?',
        a: "Founding Coaches are our early group of coaches, brought on in small cohorts so we can support everyone well and listen closely to feedback. When a cohort is full you can join the waitlist. More spots open up roughly weekly, and you'll be first in line when they do.",
      },
    ],
  },
  {
    category: 'Still need help?',
    items: [
      {
        q: "I have a question that isn't answered here.",
        a: "We'd love to hear from you. Tap “Contact Support” below to send us a question, report a bug, or suggest a feature. It comes straight to us and we'll get back to you by email.",
      },
    ],
  },
];
